/**
 * Prediction Parser Utility
 * Parses AI responses to extract individual game predictions
 * Supports both English and Portuguese (pt-BR) formats
 *
 * CRITICAL FIX (9/20/24):
 * - Added team validation to prevent AI game confusion
 * - Problem: AI sometimes predicts teams from wrong games (e.g., "Atletico Paranaense" for game 1 when they're in game 3)
 * - Solution: Validates predicted winner is actually playing in that game
 * - Auto-corrects by finding the right game when possible
 * - Falls back to N/A prediction to trigger star coin refunds when validation fails
 * - This prevents data corruption in MarketAnalytics where wrong teams showed with predictions
 *
 * Refund Integration:
 * - Failed validations return winner='N/A', confidence='N/A'
 * - useQuickPrediction.js detects N/A and refunds 7 star coins per failed prediction
 * - Bilingual error messages for Brazilian Portuguese users
 */

import { getBrazilianStatus } from '../../../utils/detectBrazil';

/**
 * Parse multiple game predictions from AI response
 * @param {string} response - Full AI response text
 * @param {Array} games - Array of games in same order as predictions
 * @returns {Object} Parsed predictions by game ID
 */
export const parseMultipleGamePredictions = (response, games) => {
  const predictions = {};
  const isBrazilian = getBrazilianStatus();
  
  // Split response by lines
  const lines = response.split('\n').filter(line => line.trim());
  
  // Look for patterns like "1: Team | XX.X | CONFIDENCE | Reason"
  // Support both English (HIGH|MED|MEDIUM|LOW) and Portuguese (ALTA|MÉDIA|BAIXA)
  // Now includes numerical confidence percentage
  const predictionPattern = /^(\d+)[:.]?\s*(.+?)\s*\|\s*([\d.]+)\s*\|\s*(HIGH|MED|MEDIUM|LOW|ALTA|MÉDIA|BAIXA)\s*\|\s*(.+)$/i;
  // Fallback pattern without numerical confidence for backward compatibility
  const legacyPattern = /^(\d+)[:.]?\s*(.+?)\s*\|\s*(HIGH|MED|MEDIUM|LOW|ALTA|MÉDIA|BAIXA)\s*\|\s*(.+)$/i;
  
  lines.forEach(line => {
    let match = line.match(predictionPattern);

    if (match) {
      // New format with numerical confidence
      const [, gameNumber, winner, numConfidence, textConfidence, reason] = match;
      const gameIndex = parseInt(gameNumber) - 1;

      if (games[gameIndex]) {
        const game = games[gameIndex];
        const gameId = game.fixture?.id || game.id;

        // Get team names for validation (handle NBA visitors/home structure)
        const homeTeam = game.teams?.home?.name || game.homeTeam || '';
        const awayTeam = game.teams?.away?.name || game.teams?.visitors?.name || game.awayTeam || '';
        const winnerTrimmed = winner.trim();

        // CRITICAL: Validate that the predicted winner is actually playing in this game
        // Check for exact match or partial match (in case of slight naming differences)
        const isHomeTeam = homeTeam && (
          homeTeam.toLowerCase() === winnerTrimmed.toLowerCase() ||
          homeTeam.toLowerCase().includes(winnerTrimmed.toLowerCase()) ||
          winnerTrimmed.toLowerCase().includes(homeTeam.toLowerCase())
        );

        const isAwayTeam = awayTeam && (
          awayTeam.toLowerCase() === winnerTrimmed.toLowerCase() ||
          awayTeam.toLowerCase().includes(winnerTrimmed.toLowerCase()) ||
          winnerTrimmed.toLowerCase().includes(awayTeam.toLowerCase())
        );

        if (!isHomeTeam && !isAwayTeam) {
          // Winner doesn't match either team - AI may have confused games
          console.warn(`⚠️ AI Confusion Detected: Game ${gameNumber} predicted winner "${winnerTrimmed}" doesn't match teams: ${homeTeam} vs ${awayTeam}`);
          console.warn(`Attempting to find correct game for prediction...`);

          // Try to find the correct game that has this team
          let correctGameIndex = -1;
          for (let i = 0; i < games.length; i++) {
            const checkGame = games[i];
            const checkHome = checkGame.teams?.home?.name || checkGame.homeTeam || '';
            const checkAway = checkGame.teams?.away?.name || checkGame.teams?.visitors?.name || checkGame.awayTeam || '';

            const matchesHome = checkHome && (
              checkHome.toLowerCase() === winnerTrimmed.toLowerCase() ||
              checkHome.toLowerCase().includes(winnerTrimmed.toLowerCase()) ||
              winnerTrimmed.toLowerCase().includes(checkHome.toLowerCase())
            );

            const matchesAway = checkAway && (
              checkAway.toLowerCase() === winnerTrimmed.toLowerCase() ||
              checkAway.toLowerCase().includes(winnerTrimmed.toLowerCase()) ||
              winnerTrimmed.toLowerCase().includes(checkAway.toLowerCase())
            );

            if (matchesHome || matchesAway) {
              correctGameIndex = i;
              console.warn(`✅ Found correct game at index ${i + 1}: ${checkHome} vs ${checkAway}`);
              break;
            }
          }

          // If we found the correct game, use it instead
          if (correctGameIndex >= 0) {
            const correctGame = games[correctGameIndex];
            const correctGameId = correctGame.fixture?.id || correctGame.id;

            // Normalize confidence levels (Portuguese to English)
            let normalizedConfidence = textConfidence.toUpperCase();
            if (normalizedConfidence === 'ALTA') normalizedConfidence = 'HIGH';
            else if (normalizedConfidence === 'MÉDIA') normalizedConfidence = 'MED';
            else if (normalizedConfidence === 'BAIXA') normalizedConfidence = 'LOW';
            else if (normalizedConfidence === 'MEDIUM') normalizedConfidence = 'MED';

            // Parse numerical confidence and ensure it's within bounds
            const numericConfidence = Math.min(99.9, Math.max(0.1, parseFloat(numConfidence)));

            predictions[correctGameId] = {
              winner: winnerTrimmed,
              confidence: normalizedConfidence,
              confidenceNumeric: numericConfidence,
              reason: reason.trim(),
              full: `${winnerTrimmed} | ${numericConfidence} | ${textConfidence.toUpperCase()} | ${reason.trim()}`,
              wasCorrected: true,
              originalGameNumber: gameNumber
            };

            return; // Skip the regular assignment below
          } else {
            // Couldn't find the team in any game - mark as N/A to trigger refund
            console.error(`❌ Could not find game for team "${winnerTrimmed}" - marking as N/A for refund`);

            // Set as N/A prediction to trigger refund mechanism
            const unavailableReason = isBrazilian
              ? `Previsão indisponível - ${winnerTrimmed} não encontrado na partida`
              : `Prediction unavailable - ${winnerTrimmed} not found in match`;

            predictions[gameId] = {
              winner: 'N/A',
              confidence: 'N/A',
              confidenceNumeric: 0,
              reason: unavailableReason,
              full: `N/A | N/A | ${isBrazilian ? 'Falha na validação' : 'Prediction failed validation'}`,
              validationFailed: true
            };
            return;
          }
        }

        // Normalize confidence levels (Portuguese to English)
        let normalizedConfidence = textConfidence.toUpperCase();
        if (normalizedConfidence === 'ALTA') normalizedConfidence = 'HIGH';
        else if (normalizedConfidence === 'MÉDIA') normalizedConfidence = 'MED';
        else if (normalizedConfidence === 'BAIXA') normalizedConfidence = 'LOW';
        else if (normalizedConfidence === 'MEDIUM') normalizedConfidence = 'MED';

        // Parse numerical confidence and ensure it's within bounds
        const numericConfidence = Math.min(99.9, Math.max(0.1, parseFloat(numConfidence)));

        predictions[gameId] = {
          winner: winnerTrimmed,
          confidence: normalizedConfidence,
          confidenceNumeric: numericConfidence,
          reason: reason.trim(),
          full: `${winnerTrimmed} | ${numericConfidence} | ${textConfidence.toUpperCase()} | ${reason.trim()}`
        };
      }
    } else {
      // Try legacy format without numerical confidence
      match = line.match(legacyPattern);
      if (match) {
        const [, gameNumber, winner, confidence, reason] = match;
        const gameIndex = parseInt(gameNumber) - 1;

        if (games[gameIndex]) {
          const game = games[gameIndex];
          const gameId = game.fixture?.id || game.id;

          // Get team names for validation (handle NBA visitors/home structure)
          const homeTeam = game.teams?.home?.name || game.homeTeam || '';
          const awayTeam = game.teams?.away?.name || game.teams?.visitors?.name || game.awayTeam || '';
          const winnerTrimmed = winner.trim();

          // CRITICAL: Validate that the predicted winner is actually playing in this game
          // Check for exact match or partial match (in case of slight naming differences)
          const isHomeTeam = homeTeam && (
            homeTeam.toLowerCase() === winnerTrimmed.toLowerCase() ||
            homeTeam.toLowerCase().includes(winnerTrimmed.toLowerCase()) ||
            winnerTrimmed.toLowerCase().includes(homeTeam.toLowerCase())
          );

          const isAwayTeam = awayTeam && (
            awayTeam.toLowerCase() === winnerTrimmed.toLowerCase() ||
            awayTeam.toLowerCase().includes(winnerTrimmed.toLowerCase()) ||
            winnerTrimmed.toLowerCase().includes(awayTeam.toLowerCase())
          );

          if (!isHomeTeam && !isAwayTeam) {
            // Winner doesn't match either team - AI may have confused games
            console.warn(`⚠️ AI Confusion Detected (Legacy): Game ${gameNumber} predicted winner "${winnerTrimmed}" doesn't match teams: ${homeTeam} vs ${awayTeam}`);
            console.warn(`Attempting to find correct game for prediction...`);

            // Try to find the correct game that has this team
            let correctGameIndex = -1;
            for (let i = 0; i < games.length; i++) {
              const checkGame = games[i];
              const checkHome = checkGame.teams?.home?.name || checkGame.homeTeam || '';
              const checkAway = checkGame.teams?.away?.name || checkGame.teams?.visitors?.name || checkGame.awayTeam || '';

              const matchesHome = checkHome && (
                checkHome.toLowerCase() === winnerTrimmed.toLowerCase() ||
                checkHome.toLowerCase().includes(winnerTrimmed.toLowerCase()) ||
                winnerTrimmed.toLowerCase().includes(checkHome.toLowerCase())
              );

              const matchesAway = checkAway && (
                checkAway.toLowerCase() === winnerTrimmed.toLowerCase() ||
                checkAway.toLowerCase().includes(winnerTrimmed.toLowerCase()) ||
                winnerTrimmed.toLowerCase().includes(checkAway.toLowerCase())
              );

              if (matchesHome || matchesAway) {
                correctGameIndex = i;
                console.warn(`✅ Found correct game at index ${i + 1}: ${checkHome} vs ${checkAway}`);
                break;
              }
            }

            // If we found the correct game, use it instead
            if (correctGameIndex >= 0) {
              const correctGame = games[correctGameIndex];
              const correctGameId = correctGame.fixture?.id || correctGame.id;

              // Normalize confidence levels (Portuguese to English)
              let normalizedConfidence = confidence.toUpperCase();
              if (normalizedConfidence === 'ALTA') normalizedConfidence = 'HIGH';
              else if (normalizedConfidence === 'MÉDIA') normalizedConfidence = 'MED';
              else if (normalizedConfidence === 'BAIXA') normalizedConfidence = 'LOW';
              else if (normalizedConfidence === 'MEDIUM') normalizedConfidence = 'MED';

              // Calculate numeric confidence based on text level
              let numericConfidence = 50.0; // Default
              if (normalizedConfidence === 'HIGH') numericConfidence = 75.0;
              else if (normalizedConfidence === 'MED') numericConfidence = 50.0;
              else if (normalizedConfidence === 'LOW') numericConfidence = 25.0;

              predictions[correctGameId] = {
                winner: winnerTrimmed,
                confidence: normalizedConfidence,
                confidenceNumeric: numericConfidence,
                reason: reason.trim(),
                full: `${winnerTrimmed} | ${confidence.toUpperCase()} | ${reason.trim()}`,
                wasCorrected: true,
                originalGameNumber: gameNumber
              };

              return; // Skip the regular assignment below
            } else {
              // Couldn't find the team in any game - mark as N/A to trigger refund
              console.error(`❌ Could not find game for team "${winnerTrimmed}" - marking as N/A for refund (legacy format)`);

              // Set as N/A prediction to trigger refund mechanism
              const unavailableReason = isBrazilian
                ? `Previsão indisponível - ${winnerTrimmed} não encontrado na partida`
                : `Prediction unavailable - ${winnerTrimmed} not found in match`;

              predictions[gameId] = {
                winner: 'N/A',
                confidence: 'N/A',
                confidenceNumeric: 0,
                reason: unavailableReason,
                full: `N/A | N/A | ${isBrazilian ? 'Falha na validação' : 'Prediction failed validation'}`,
                validationFailed: true
              };
              return;
            }
          }

          // Normalize confidence levels (Portuguese to English)
          let normalizedConfidence = confidence.toUpperCase();
          if (normalizedConfidence === 'ALTA') normalizedConfidence = 'HIGH';
          else if (normalizedConfidence === 'MÉDIA') normalizedConfidence = 'MED';
          else if (normalizedConfidence === 'BAIXA') normalizedConfidence = 'LOW';
          else if (normalizedConfidence === 'MEDIUM') normalizedConfidence = 'MED';

          // Calculate numeric confidence based on text level
          let numericConfidence = 50.0; // Default
          if (normalizedConfidence === 'HIGH') numericConfidence = 75.0;
          else if (normalizedConfidence === 'MED') numericConfidence = 50.0;
          else if (normalizedConfidence === 'LOW') numericConfidence = 25.0;

          predictions[gameId] = {
            winner: winnerTrimmed,
            confidence: normalizedConfidence,
            confidenceNumeric: numericConfidence,
            reason: reason.trim(),
            full: `${winnerTrimmed} | ${confidence.toUpperCase()} | ${reason.trim()}`
          };
        }
      } else {
        // Try to match Portuguese format without game number prefix
        // e.g., "New York Red Bulls    MÉDIA    Defesa sólida e momento melhor"
        const ptMatch = line.match(/^(.+?)\s+(ALTA|MÉDIA|BAIXA|HIGH|MED|MEDIUM|LOW)\s+(.+)$/i);
        if (ptMatch) {
          const [, winner, confidence, reason] = ptMatch;

          // Find which game this might be for based on team names
          games.forEach((game) => {
            const homeTeam = game.teams?.home?.name || game.homeTeam;
            const awayTeam = game.teams?.away?.name || game.teams?.visitors?.name || game.awayTeam;

            if (winner.includes(homeTeam) || winner.includes(awayTeam) ||
                homeTeam?.includes(winner.trim()) || awayTeam?.includes(winner.trim())) {
              const gameId = game.fixture?.id || game.id;

              // Normalize confidence levels
              let normalizedConfidence = confidence.toUpperCase();
              if (normalizedConfidence === 'ALTA') normalizedConfidence = 'HIGH';
              else if (normalizedConfidence === 'MÉDIA') normalizedConfidence = 'MED';
              else if (normalizedConfidence === 'BAIXA') normalizedConfidence = 'LOW';
              else if (normalizedConfidence === 'MEDIUM') normalizedConfidence = 'MED';

              // Calculate numeric confidence based on text level
              let numericConfidence = 50.0; // Default
              if (normalizedConfidence === 'HIGH') numericConfidence = 75.0;
              else if (normalizedConfidence === 'MED') numericConfidence = 50.0;
              else if (normalizedConfidence === 'LOW') numericConfidence = 25.0;

              predictions[gameId] = {
                winner: winner.trim(),
                confidence: normalizedConfidence,
                confidenceNumeric: numericConfidence,
                reason: reason.trim(),
                full: line.trim()
              };
            }
          });
        }
      }
    }
  });
  
  // Fallback: If no structured format found, assign full response to all games
  if (Object.keys(predictions).length === 0) {
    // Check for failure patterns in the response
    const failurePatterns = [
      /NÃO POSSÍVEL/i,
      /Sem dados/i,
      /Cannot analyze/i,
      /Not possible/i,
      /Insufficient data/i
    ];
    
    const hasFailurePattern = failurePatterns.some(pattern => pattern.test(response));
    
    games.forEach(game => {
      const gameId = game.fixture?.id || game.id;
      predictions[gameId] = {
        full: response,
        winner: hasFailurePattern ? 'N/A' : 'See analysis',
        confidence: 'N/A',
        reason: 'Full analysis above'
      };
    });
  }
  
  return predictions;
};

/**
 * Parse single game prediction
 * @param {string} response - AI response for single game
 * @returns {Object} Parsed prediction
 */
export const parseSingleGamePrediction = (response) => {
  // Look for pattern: "Team | XX.X | CONFIDENCE | Reason" (new format)
  // Or fallback to: "Team | CONFIDENCE | Reason" (legacy format)
  // Support both English (HIGH|MED|MEDIUM|LOW) and Portuguese (ALTA|MÉDIA|BAIXA)
  const newMatch = response.match(/(.+?)\s*\|\s*([\d.]+)\s*\|\s*(HIGH|MED|MEDIUM|LOW|ALTA|MÉDIA|BAIXA)\s*\|\s*(.+)/i);
  const legacyMatch = response.match(/(.+?)\s*\|\s*(HIGH|MED|MEDIUM|LOW|ALTA|MÉDIA|BAIXA)\s*\|\s*(.+)/i);

  if (newMatch) {
    // New format with numerical confidence
    const [, winner, numConfidence, textConfidence, reason] = newMatch;

    // Normalize confidence levels (Portuguese to English)
    let normalizedConfidence = textConfidence.toUpperCase();
    if (normalizedConfidence === 'ALTA') normalizedConfidence = 'HIGH';
    else if (normalizedConfidence === 'MÉDIA') normalizedConfidence = 'MED';
    else if (normalizedConfidence === 'BAIXA') normalizedConfidence = 'LOW';
    else if (normalizedConfidence === 'MEDIUM') normalizedConfidence = 'MED';

    // Parse numerical confidence and ensure it's within bounds
    const numericConfidence = Math.min(99.9, Math.max(0.1, parseFloat(numConfidence)));

    return {
      winner: winner.trim(),
      confidence: normalizedConfidence,
      confidenceNumeric: numericConfidence,
      reason: reason.trim(),
      full: response.trim()
    };
  } else if (legacyMatch) {
    // Legacy format without numerical confidence
    const [, winner, confidence, reason] = legacyMatch;

    // Normalize confidence levels (Portuguese to English)
    let normalizedConfidence = confidence.toUpperCase();
    if (normalizedConfidence === 'ALTA') normalizedConfidence = 'HIGH';
    else if (normalizedConfidence === 'MÉDIA') normalizedConfidence = 'MED';
    else if (normalizedConfidence === 'BAIXA') normalizedConfidence = 'LOW';
    else if (normalizedConfidence === 'MEDIUM') normalizedConfidence = 'MED';

    // Calculate numeric confidence based on text level
    let numericConfidence = 50.0; // Default
    if (normalizedConfidence === 'HIGH') numericConfidence = 75.0;
    else if (normalizedConfidence === 'MED') numericConfidence = 50.0;
    else if (normalizedConfidence === 'LOW') numericConfidence = 25.0;

    return {
      winner: winner.trim(),
      confidence: normalizedConfidence,
      confidenceNumeric: numericConfidence,
      reason: reason.trim(),
      full: response.trim()
    };
  }
  
  // Check for Portuguese/multi-language failure patterns
  const failurePatterns = [
    /NÃO POSSÍVEL/i,
    /Sem dados/i,
    /Cannot analyze/i,
    /Not possible/i,
    /Insufficient data/i
  ];
  
  const hasFailurePattern = failurePatterns.some(pattern => pattern.test(response));
  
  // Fallback: return full response
  return {
    full: response.trim(),
    winner: hasFailurePattern ? 'N/A' : 'See analysis',
    confidence: 'N/A',
    reason: response.trim()
  };
};

/**
 * Format prediction for display
 * @param {Object} prediction - Parsed prediction object
 * @param {boolean} compact - Whether to show compact version
 * @returns {string} Formatted prediction text
 */
export const formatPredictionDisplay = (prediction, compact = true) => {
  if (!prediction) return 'No prediction available';
  
  if (compact && prediction.winner && prediction.confidence) {
    return `${prediction.winner} (${prediction.confidence})`;
  }
  
  return prediction.full || 'No prediction available';
};